import type { ReactNode } from 'react';

/**
 * Batch 5: expanded to the 12-domain accent system (see
 * app/dashboard/navigation.ts's AccentTone for the full rationale).
 * 'management' kept as a legacy alias.
 */
export type BadgeTone =
  | 'neutral'
  | 'security'
  | 'automod'
  | 'moderation'
  | 'management'
  | 'community'
  | 'automation'
  | 'ai'
  | 'tickets'
  | 'analytics'
  | 'settings'
  | 'logs'
  | 'backups'
  | 'help';

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
}

const toneClasses: Record<BadgeTone, string> = {
  neutral: 'bg-bg-elevated text-text-secondary border-border-subtle',
  security: 'bg-accent-security-50 text-accent-security-400 border-accent-security-border',
  automod: 'bg-accent-automod-50 text-accent-automod-400 border-accent-automod-border',
  moderation: 'bg-accent-moderation-50 text-accent-moderation-400 border-accent-moderation-border',
  management: 'bg-accent-management-50 text-accent-management-400 border-accent-management-border',
  community: 'bg-accent-community-50 text-accent-community-400 border-accent-community-border',
  automation: 'bg-accent-automation-50 text-accent-automation-400 border-accent-automation-border',
  ai: 'bg-accent-ai-50 text-accent-ai-400 border-accent-ai-border',
  tickets: 'bg-accent-tickets-50 text-accent-tickets-400 border-accent-tickets-border',
  analytics: 'bg-accent-analytics-50 text-accent-analytics-400 border-accent-analytics-border',
  settings: 'bg-accent-settings-50 text-accent-settings-400 border-accent-settings-border',
  logs: 'bg-accent-logs-50 text-accent-logs-400 border-accent-logs-border',
  backups: 'bg-accent-backups-50 text-accent-backups-400 border-accent-backups-border',
  help: 'bg-accent-help-50 text-accent-help-400 border-accent-help-border',
};

/**
 * Never a solid fill — always the subtle-bg + border treatment.
 * docs/DESIGN_SYSTEM.md §4.8.
 */
export function Badge({ children, tone = 'neutral' }: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-sm border px-2 py-0.5 text-micro uppercase',
        toneClasses[tone],
      ].join(' ')}
    >
      {children}
    </span>
  );
}
